import { useMutation } from "@tanstack/react-query";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";

export interface TestSendInput {
  /** The typed test number; empty falls back to the store's owner_test_phone. */
  to: string;
  messageBody: string;
  promotionId: string | null;
  imageUrl: string | null;
  expirationDays: number | null;
  timerMinutes: number | null;
}

export interface TestSendOutcome {
  /** The number the backend actually texted. Null when the deployed
   *  sms-actions predates the typed-number change and ignored `to` — the UI
   *  must then tell the user the test went to owner_test_phone instead. */
  sentTo: string | null;
  messageId: string;
}

/** Calls sms-actions' test_send: a real text with a real working offer link,
 *  exempt from consent/audience rules but not the kill switch, rate-limited
 *  per store on the backend. */
export const useTestSend = () => {
  const getSupabaseClient = useSupabaseClient();

  return useMutation<TestSendOutcome, Error, TestSendInput>({
    mutationFn: async (input) => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("sms-actions", {
        body: {
          action: "test_send",
          campaign: {
            message_body: input.messageBody,
            promotion_id: input.promotionId,
            image_url: input.imageUrl,
            expiration_days: input.expirationDays,
            timer_minutes: input.timerMinutes,
            to: input.to.trim() === "" ? null : input.to,
          },
        },
      });
      if (error) throw new Error(error.message ?? "Test send failed");
      const result = data as { ok: boolean; message_id?: string; to?: string; error?: string };
      if (!result?.ok) throw new Error(result?.error ?? "Test send failed");
      return { sentTo: result.to ?? null, messageId: result.message_id ?? "" };
    },
  });
};
